import { clsx } from "clsx"
import React, {
  createContext, forwardRef, ReactNode, useCallback, useContext, useEffect, useRef, useState
} from "react"
import { Button, Menu, MenuTrigger, Popover, Tooltip, TooltipTrigger } from "react-aria-components"

import { kInspectorPaletteAriaRole } from "../accessibility-constants"
import { useFocusTrap } from "../hooks/use-focus-trap"
import { useTileModelContext } from "../hooks/use-tile-model-context"
import { returnToTileContent } from "../hooks/use-tile-navigation"
import { useMouseTooltipRef } from "../hooks/use-mouse-tooltip-ref"
import { useRovingToolbarFocus } from "../hooks/use-roving-toolbar-focus"
import { useOutsidePointerDown } from "../hooks/use-outside-pointer-down"
import { isWithinBounds, getPaletteTopPosition } from "../utilities/view-utils"

import "./inspector-panel.scss"

// Tracks whether the enclosing InspectorMenu is currently open. Menu content can read this to skip
// expensive, selection-dependent work (e.g. O(N) selected/unselected case computations) while the
// menu is closed — otherwise an always-mounted menu observer recomputes on every selection change.
const InspectorMenuOpenContext = createContext(false)
export const useInspectorMenuOpen = () => useContext(InspectorMenuOpenContext)

// Returns an aria-label for icon-only inspector buttons/menus.
// When a visible text label exists, returns undefined (the label provides the accessible name).
// When no label exists, returns the tooltip with parenthetical keyboard shortcuts stripped
// (e.g. "bold (⌘-b)" → "bold") so screen readers announce a clean name.
function ariaLabel(label?: string, tooltip?: string) {
  if (label) return undefined
  return tooltip?.replace(/\s*\(.*\)$/, "")
}

function renderIcon(icon: ReactNode, isDecorative: boolean) {
  return isDecorative ? <span aria-hidden="true">{icon}</span> : icon
}

interface IProps {
  component?: string
  show?: boolean
  children: ReactNode
  setShowPalette?: (palette: string | undefined) => void
  toolbarAriaLabel?: string
  toolbarOrientation?: "horizontal" | "vertical"
  toolbarPersistenceKey?: string
  width?: "very-narrow" | "narrow" | "normal" | "wide"
}

const kTooltipDelay = 1000
const kTooltipPlacement = "bottom" as const
const kTooltipOffset = 15

export const InspectorPanel = forwardRef<HTMLDivElement, IProps>(function InspectorPanel({
  component, show, setShowPalette, children, toolbarAriaLabel,
  toolbarOrientation = "vertical", toolbarPersistenceKey, width
}, ref) {
  const { tileId } = useTileModelContext()
  const panelRef = useRef<HTMLDivElement | null>(null)
  const mergedRef = useCallback((node: HTMLDivElement | null) => {
    panelRef.current = node
    if (typeof ref === "function") ref(node)
    else if (ref) ref.current = node
  }, [ref])
  useOutsidePointerDown({
    ref: panelRef,
    handler: ()=> setShowPalette?.(undefined),
    enabled: !!(show && panelRef && setShowPalette),
    info: {name: "InspectorPanel", component}
  })
  const { onFocusCapture, onKeyDownCapture } = useRovingToolbarFocus({
    enabled: !!toolbarAriaLabel,
    getItems: () => {
      if (!panelRef.current) return []
      return Array.from(panelRef.current.querySelectorAll<HTMLElement>("[data-inspector-toolbar-item='true']"))
    },
    orientation: toolbarOrientation,
    persistenceKey: toolbarPersistenceKey
  })

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      // Don't handle Escape if a palette is open. Let the palette handle it first.
      // (Menus don't need a guard because React Aria renders them in a portal
      // outside this panel's DOM tree. Their Escape events don't bubble here.)
      const panel = panelRef.current
      if (!panel) return
      if (panel.querySelector(".codap-inspector-palette-wrapper")) return
      if (!tileId) return

      e.preventDefault()
      returnToTileContent(tileId)
      return
    }

    // When a palette is open, stop Tab from reaching the tile's tab trap.
    // The palette has its own focus trap via sentinels.
    if (e.key === "Tab" && panelRef.current?.querySelector(".codap-inspector-palette-wrapper")) {
      e.stopPropagation()
    }
  }, [tileId])

  const classes = clsx("inspector-panel", component, width ?? "normal")
  return (show
    ? <div
        ref={mergedRef}
        aria-label={toolbarAriaLabel}
        aria-orientation={toolbarAriaLabel ? toolbarOrientation : undefined}
        className={classes}
        data-testid={"inspector-panel"}
        onFocusCapture={onFocusCapture}
        onKeyDown={handleKeyDown}
        onKeyDownCapture={onKeyDownCapture}
        role={toolbarAriaLabel ? "toolbar" : undefined}
      >
        {children}
      </div>
    : null
  )
})

interface IInspectorButtonProps {
  "aria-controls"?: string
  "aria-expanded"?: boolean
  bottom?: boolean
  children: ReactNode
  isActive?: boolean
  isDisabled?: boolean
  label?: string
  onButtonClick?: (e: { target: Element }) => void
  onPointerDown?: (e: React.PointerEvent) => void
  testId: string
  tooltip: string
  top?: boolean
}

export const InspectorButton = forwardRef<HTMLButtonElement, IInspectorButtonProps>(function InspectorButton({
  "aria-controls": ariaControls, "aria-expanded": ariaExpanded, bottom, children,
  isActive, isDisabled, label, onButtonClick,
  onPointerDown, testId, tooltip, top
}: IInspectorButtonProps, ref) {
  const className = clsx("inspector-tool-button", { active: isActive, bottom, top })
  const hasVisibleLabel = !!label
  const { triggerRef, onMouseMove, onFocus } = useMouseTooltipRef()
  const [tooltipOpen, setTooltipOpen] = useState(false)

  const handlePress = useCallback((e: { target: Element }) => {
    setTooltipOpen(false)
    onButtonClick?.(e)
  }, [onButtonClick])

  return (
    <TooltipTrigger delay={kTooltipDelay} isOpen={tooltipOpen} onOpenChange={setTooltipOpen}>
      <Button
        aria-controls={ariaControls}
        aria-disabled={isDisabled || undefined}
        aria-expanded={ariaExpanded}
        aria-label={ariaLabel(label, tooltip)}
        className={className}
        data-inspector-toolbar-item="true"
        data-testid={testId}
        excludeFromTabOrder={isDisabled}
        onFocus={onFocus}
        onMouseMove={onMouseMove}
        onPointerDown={!isDisabled ? onPointerDown : undefined}
        onPress={!isDisabled ? handlePress : undefined}
        ref={ref}
      >
        {renderIcon(children, hasVisibleLabel)}
        {label && <span className="inspector-button-label">{label}</span>}
      </Button>
      <Tooltip
        className="inspector-tooltip"
        offset={kTooltipOffset}
        placement={kTooltipPlacement}
        triggerRef={triggerRef}
      >
        {tooltip}
      </Tooltip>
    </TooltipTrigger>
  )
})

interface IInspectorMenuProps {
  bottom?: boolean
  children: ReactNode
  icon: ReactNode
  label?: string
  onButtonClick?: () => void
  onOpen?: () => void
  testId: string
  tooltip: string
  top?: boolean
}

export const InspectorMenu = ({
  bottom, children, icon, label, onButtonClick, onOpen, testId, tooltip, top
}: IInspectorMenuProps) => {
  const classes = clsx("inspector-tool-button", "inspector-tool-menu", { bottom, top })
  const hasVisibleLabel = !!label
  const { triggerRef, onMouseMove, onFocus } = useMouseTooltipRef()
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const menuOpenRef = useRef(false)
  const [menuOpen, setMenuOpen] = useState(false)

  const handleMenuOpenChange = useCallback((openMenu: boolean) => {
    menuOpenRef.current = openMenu
    setMenuOpen(openMenu)
    if (openMenu) {
      // Close any tooltips when a menu opens.
      setTooltipOpen(false)
      onButtonClick?.()
      onOpen?.()
    }
  }, [onButtonClick, onOpen])

  const handleTooltipOpenChange = useCallback((openTooltip: boolean) => {
    // Prevent tooltip from opening while a menu is open.
    if (openTooltip && menuOpenRef.current) return

    setTooltipOpen(openTooltip)
  }, [])

  return (
    <TooltipTrigger delay={kTooltipDelay} isOpen={tooltipOpen} onOpenChange={handleTooltipOpenChange}>
      <MenuTrigger onOpenChange={handleMenuOpenChange}>
        <Button
          aria-label={ariaLabel(label, tooltip)}
          className={classes}
          data-inspector-toolbar-item="true"
          data-testid={testId}
          onFocus={onFocus}
          onMouseMove={onMouseMove}
        >
          {renderIcon(icon, hasVisibleLabel)}
          {label && <span className="inspector-button-label">{label}</span>}
        </Button>
        <InspectorMenuOpenContext.Provider value={menuOpen}>
          {children}
        </InspectorMenuOpenContext.Provider>
      </MenuTrigger>
      <Tooltip
        className="inspector-tooltip"
        offset={kTooltipOffset}
        placement={kTooltipPlacement}
        triggerRef={triggerRef}
      >
        {tooltip}
      </Tooltip>
    </TooltipTrigger>
  )
}

interface IInspectorMenuContentProps {
  children: ReactNode
  "data-testid"?: string
}

export function InspectorMenuContent({ children, ...props }: IInspectorMenuContentProps) {
  return (
    <Popover className="inspector-menu-popover">
      <Menu className="inspector-menu-list" {...props}>
        {children}
      </Menu>
    </Popover>
  )
}

interface IInspectorPalette {
  children: ReactNode
  Icon?: ReactNode
  id?: string
  title: string
  panelRect?: DOMRect
  buttonRect?: DOMRect
  setShowPalette: (palette: string | undefined) => void
}

export const InspectorPalette = ({children, Icon, id, title, panelRect, buttonRect,
     setShowPalette}:IInspectorPalette) => {
  const pointerSize = 10
  const panelTop = panelRect?.top || 0
  const panelRight = panelRect?.right || 0
  const buttonTop = buttonRect?.top || 0
  const buttonHeight = buttonRect?.height || 52
  const [paletteWidth, setPaletteWidth] = useState(0)
  const [paletteHeight, setPaletteHeight] = useState(0)
  const paletteRef = useRef<HTMLDivElement>(null)
  useFocusTrap(paletteRef)
  const pointerRef = useRef<HTMLDivElement>(null)
  const viewportEl = paletteRef.current?.closest(".tile-row")
  const [inBounds, setInBounds] = useState(() => isWithinBounds(panelRight, paletteRef.current))
  const tempPaletteTop = paletteRef.current?.getBoundingClientRect().top
  const pointerTop = buttonTop - panelTop + buttonHeight / 2 - pointerSize
  const pointerMidpoint = pointerTop + pointerSize
  const paletteTop = (tempPaletteTop && paletteHeight) &&
    getPaletteTopPosition(tempPaletteTop, paletteHeight, pointerMidpoint)
  const headerId = title ? `palette-header-${title.replace(/\s+/g, "-").toLowerCase()}` : undefined
  useEffect(()=> {
    const observer = viewportEl && new ResizeObserver(() => {
      if (panelRight && paletteRef.current) {
        setInBounds(isWithinBounds(panelRight, paletteRef.current))
      }
    })
    viewportEl && observer?.observe(viewportEl)
    return () => observer?.disconnect()
  }, [panelRight, viewportEl])

  // Track palette height changes so positioning updates when content shrinks/grows
  useEffect(() => {
    const el = paletteRef.current
    if (!el) return
    const observer = new ResizeObserver(() => {
      setPaletteHeight(el.offsetHeight)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      setShowPalette(undefined)
    }
  }

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null
    if (paletteRef.current) {
      setPaletteWidth(paletteRef.current.offsetWidth)
      paletteRef.current.focus()
    }
    return () => {
      previousFocus?.focus()
    }
  }, [])

  const panelWidth = panelRect?.width || 72
  const paletteLeft = inBounds ? panelWidth - 5 : -(paletteWidth - 5)
  const wrapperStyle = {top: paletteTop || 0, left: paletteLeft, zIndex: 250}
  const pointerStyle = inBounds
    ? { left: -(pointerSize - 1) }
    : { right: -(pointerSize - 1) }
  return (
    <div className="codap-inspector-palette-wrapper" style={wrapperStyle}>
      <div ref={pointerRef} className={`palette-pointer ${inBounds ? "arrow-left" : "arrow-right"}`}
          style={{top: pointerTop - (paletteTop || 0), ...pointerStyle}} />
      <div ref={paletteRef} className="codap-inspector-palette" id={id} tabIndex={-1}
          role={kInspectorPaletteAriaRole} aria-labelledby={headerId}
          data-testid="codap-inspector-palette" onKeyDown={handleKeyDown}>
        <PaletteHeader id={headerId} Icon={Icon} title={title} />
        {children}
      </div>
    </div>
  )
}


interface IPaletteHeaderProps {
  id?: string
  Icon?: ReactNode
  title: string
}

function PaletteHeader({ id, Icon, title }: IPaletteHeaderProps) {
  return (
    <header id={id} className="codap-inspector-palette-header" data-testid="codap-inspector-palette-header">
      {Icon && <span className="codap-inspector-palette-icon" aria-hidden="true">{Icon}</span>}
      <span className="codap-inspector-palette-header-title">{title}</span>
    </header>
  )
}
